import { createAssociatedTokenAccountIdempotentInstruction, NATIVE_MINT } from '@solana/spl-token';
import { PublicKey, TransactionInstruction } from '@solana/web3.js';
import {
  ASSOCIATED_TOKEN_PROGRAM_ID,
  SYSTEM_PROGRAM_ID,
  TOKEN_PROGRAM_ID,
} from '../constants/programs.js';
import { decodeSponsorshipInstruction } from './layout.js';

export interface CanonicalAtaCreate {
  readonly payer: TransactionInstruction['keys'][number];
  readonly ata: TransactionInstruction['keys'][number];
  readonly owner: TransactionInstruction['keys'][number];
  readonly mint: TransactionInstruction['keys'][number];
  readonly systemProgram: TransactionInstruction['keys'][number];
  readonly tokenProgram: TransactionInstruction['keys'][number];
}

export interface RelayAtaSponsorshipInput {
  readonly instructions: readonly TransactionInstruction[];
  readonly relayFeePayer: PublicKey;
  readonly wallet: PublicKey;
}

export interface RelayAtaSponsorshipResult {
  readonly instructions: TransactionInstruction[];
  readonly sponsoredAtas: readonly PublicKey[];
}

/**
 * Returns the exact standard idempotent ATA create shape or null. The ATA
 * address is derived from owner, token program, and mint before sponsorship.
 */
export function decodeCanonicalIdempotentAtaCreate(
  instruction: TransactionInstruction,
): CanonicalAtaCreate | null {
  return decodeCanonicalAtaCreate(instruction);
}

/**
 * Decodes a canonical idempotent ATA create after Solana has widened account
 * privileges for the whole compiled transaction. The owner must still be the
 * authenticated signer, while the standard six-account ordering, program IDs,
 * data, and ATA derivation remain exact.
 */
export function decodeIdempotentAtaCreateFromCompiledMessage(
  instruction: TransactionInstruction,
  authenticatedWallet: PublicKey,
): CanonicalAtaCreate | null {
  const create = decodeAtaCreate(instruction);
  if (
    create === null ||
    !create.payer.isSigner ||
    !create.payer.isWritable ||
    !create.ata.isWritable ||
    !create.owner.pubkey.equals(authenticatedWallet) ||
    !create.owner.isSigner ||
    create.systemProgram.isSigner ||
    create.systemProgram.isWritable ||
    create.tokenProgram.isSigner ||
    create.tokenProgram.isWritable ||
    create.mint.isSigner
  ) {
    return null;
  }
  return create;
}

function decodeCanonicalAtaCreate(instruction: TransactionInstruction): CanonicalAtaCreate | null {
  const create = decodeAtaCreate(instruction);
  if (create === null) return null;
  const expected = createAtaInstruction(create, create.payer.pubkey);
  return hasExactInstructionShape(instruction, expected) ? create : null;
}

function decodeAtaCreate(instruction: TransactionInstruction): CanonicalAtaCreate | null {
  if (!instruction.programId.equals(ASSOCIATED_TOKEN_PROGRAM_ID) || instruction.keys.length !== 6) {
    return null;
  }
  const [payer, ata, owner, mint, systemProgram, tokenProgram] = instruction.keys;
  if (
    payer === undefined ||
    ata === undefined ||
    owner === undefined ||
    mint === undefined ||
    systemProgram === undefined ||
    tokenProgram === undefined ||
    !systemProgram.pubkey.equals(SYSTEM_PROGRAM_ID) ||
    !tokenProgram.pubkey.equals(TOKEN_PROGRAM_ID) ||
    !ata.pubkey.equals(deriveAta(owner.pubkey, tokenProgram.pubkey, mint.pubkey))
  ) {
    return null;
  }
  const create = { payer, ata, owner, mint, systemProgram, tokenProgram };
  return bytesEqual(instruction.data, createAtaInstruction(create, payer.pubkey).data)
    ? create
    : null;
}

/**
 * Finds wallet ATAs that may need relay-funded creation. This intentionally
 * excludes host-cached ATAs; callers use it to serialize only a first creation.
 */
export async function relayAtaSponsorshipCandidates(
  input: Pick<RelayAtaSponsorshipInput, 'instructions' | 'wallet'>,
): Promise<readonly PublicKey[]> {
  return (await collectRelayAtaSponsorshipCandidates(input)).map((candidate) => candidate.ata);
}

async function collectRelayAtaSponsorshipCandidates(
  input: Pick<RelayAtaSponsorshipInput, 'instructions' | 'wallet'>,
): Promise<RelayAtaCandidate[]> {
  const userFundedAtaTargets = await userFundedBuyQuoteAtaTargets(input.instructions, input.wallet);
  return collectCandidates(input.instructions, input.wallet, userFundedAtaTargets);
}

/**
 * Replaces the payer on one canonical idempotent ATA creation per eligible
 * target. Existing accounts remain safe no-ops, so no account lookup, race
 * handling, or CloseAccount authority transfer is needed.
 */
export async function sponsorRelayAtaRent(
  input: RelayAtaSponsorshipInput,
): Promise<RelayAtaSponsorshipResult> {
  const candidates = await collectRelayAtaSponsorshipCandidates(input);
  const sponsorableAtas = new Set(candidates.map((candidate) => candidate.ata.toBase58()));
  const sponsoredAtas: PublicKey[] = [];
  const instructions: TransactionInstruction[] = [];
  for (const instruction of input.instructions) {
    const create = decodeCanonicalIdempotentAtaCreate(instruction);
    if (create === null || !sponsorableAtas.has(create.ata.pubkey.toBase58())) {
      instructions.push(await sponsorWalletInitPosition(instruction, input));
      continue;
    }
    sponsorableAtas.delete(create.ata.pubkey.toBase58());
    sponsoredAtas.push(create.ata.pubkey);
    instructions.push(createAtaInstruction(create, input.relayFeePayer));
  }
  return { instructions, sponsoredAtas };
}

/**
 * Sell-cap program: moves the rent of the wallet's own `init_position` to the
 * relay, which the relay policy accepts only as payer with the wallet as owner.
 */
async function sponsorWalletInitPosition(
  instruction: TransactionInstruction,
  input: RelayAtaSponsorshipInput,
): Promise<TransactionInstruction> {
  const decoded = await decodeSponsorshipInstruction(instruction);
  const payerIndex = decoded?.layout.accountIndex('payer') ?? null;
  if (
    decoded?.layout.name !== 'initPosition' ||
    payerIndex === null ||
    decoded.account('payer')?.pubkey.equals(input.wallet) !== true ||
    decoded.account('owner')?.pubkey.equals(input.wallet) !== true
  ) {
    return instruction;
  }
  return new TransactionInstruction({
    programId: instruction.programId,
    data: Buffer.from(instruction.data),
    keys: instruction.keys.map((key, index) =>
      index === payerIndex ? { ...key, pubkey: input.relayFeePayer } : key,
    ),
  });
}

interface RelayAtaCandidate {
  readonly ata: PublicKey;
  readonly mint: PublicKey;
  readonly tokenProgram: PublicKey;
}

function collectCandidates(
  instructions: readonly TransactionInstruction[],
  wallet: PublicKey,
  userFundedAtaTargets: ReadonlySet<string>,
): RelayAtaCandidate[] {
  const seen = new Set<string>();
  const candidates: RelayAtaCandidate[] = [];
  for (const instruction of instructions) {
    const create = decodeCanonicalIdempotentAtaCreate(instruction);
    if (
      create === null ||
      !create.payer.pubkey.equals(wallet) ||
      !create.owner.pubkey.equals(wallet) ||
      isNativeMint(create.mint.pubkey) ||
      userFundedAtaTargets.has(create.ata.pubkey.toBase58()) ||
      seen.has(create.ata.pubkey.toBase58())
    ) {
      continue;
    }
    seen.add(create.ata.pubkey.toBase58());
    candidates.push({
      ata: create.ata.pubkey,
      mint: create.mint.pubkey,
      tokenProgram: create.tokenProgram.pubkey,
    });
  }
  return candidates;
}

async function userFundedBuyQuoteAtaTargets(
  instructions: readonly TransactionInstruction[],
  wallet: PublicKey,
): Promise<ReadonlySet<string>> {
  const targets = new Set<string>();
  const priorSellTargets = new Set<string>();
  for (const instruction of instructions) {
    const decoded = await decodeSponsorshipInstruction(instruction);
    if (decoded === null) continue;
    const trader = decoded.account('trader');
    const quoteAta = decoded.account('trader_quote_account');
    if (!trader?.pubkey.equals(wallet) || quoteAta === null) continue;
    if (decoded.layout.name === 'sell') {
      priorSellTargets.add(quoteAta.pubkey.toBase58());
      continue;
    }
    if (decoded.layout.name === 'buy' && !priorSellTargets.has(quoteAta.pubkey.toBase58())) {
      targets.add(quoteAta.pubkey.toBase58());
    }
  }
  return targets;
}

function createAtaInstruction(
  create: CanonicalAtaCreate,
  payer: PublicKey,
): TransactionInstruction {
  return createAssociatedTokenAccountIdempotentInstruction(
    payer,
    create.ata.pubkey,
    create.owner.pubkey,
    create.mint.pubkey,
    create.tokenProgram.pubkey,
    ASSOCIATED_TOKEN_PROGRAM_ID,
  );
}

function hasExactInstructionShape(
  instruction: TransactionInstruction,
  expected: TransactionInstruction,
): boolean {
  return (
    instruction.programId.equals(expected.programId) &&
    bytesEqual(instruction.data, expected.data) &&
    instruction.keys.length === expected.keys.length &&
    instruction.keys.every((key, index) => {
      const expectedKey = expected.keys[index];
      return (
        expectedKey !== undefined &&
        key.pubkey.equals(expectedKey.pubkey) &&
        key.isSigner === expectedKey.isSigner &&
        key.isWritable === expectedKey.isWritable
      );
    })
  );
}

function deriveAta(owner: PublicKey, tokenProgram: PublicKey, mint: PublicKey): PublicKey {
  const [ata] = PublicKey.findProgramAddressSync(
    [owner.toBuffer(), tokenProgram.toBuffer(), mint.toBuffer()],
    ASSOCIATED_TOKEN_PROGRAM_ID,
  );
  return ata;
}

function isNativeMint(mint: PublicKey): boolean {
  return mint.equals(NATIVE_MINT);
}

function bytesEqual(left: Uint8Array, right: Uint8Array): boolean {
  return left.length === right.length && left.every((byte, index) => byte === right[index]);
}
